import getCookie from "./csrftoken";

document.addEventListener("DOMContentLoaded", function(event) {
    var editorWrapper = document.querySelector('.wmd-editor');
    const form = document.querySelector('form');
    if (editorWrapper) {

        var textElement = editorWrapper.querySelector('.wmd-input');
        if (textElement) {
            var markdownInput = document.createElement('o3dl-md-input');
            markdownInput.value = textElement.value;
            textElement.remove();
            editorWrapper.appendChild(markdownInput);
            // We need to explicitly tie the formdata event to the form for our custom element to work with the native form
            form.addEventListener('formdata', ({formData}) => {
              // https://developer.mozilla.org/docs/Web/API/FormData
              formData.append(textElement.name, markdownInput.value);
            });
        }
    }
});

import { LitElement, html, css } from 'lit';
import {unsafeHTML} from "lit-html/directives/unsafe-html.js";

class MarkdownInput extends LitElement {
    // A lot of the functionality to tie this thing into the native form is based on the following article:
    // https://web.dev/articles/more-capable-form-controls
    static properties = {
        value_: { type: String },
        isLoading: { type: Boolean },
        previewMode: { type: Boolean },
        previewContent: { type: String },
        plugins: { type: Array },
        selectionStart: { type: Number },
        selectionEnd: { type: Number },
        undoStack: { type: Array },
        redoStack: { type: Array },
        preventCursorReset: { type: Boolean },
    };
    static formAssociated = true;

    get value() { return this.value_; }
    set value(v) { this.value_ = v; }
    get form() { return this.internals_.form; }
    get name() { return this.getAttribute('name'); }
    get type() { return this.localName; }
    get validity() {return this.internals_.validity; }
    get validationMessage() {return this.internals_.validationMessage; }
    get willValidate() {return this.internals_.willValidate; }

    checkValidity() { return this.internals_.checkValidity(); }
    reportValidity() {return this.internals_.reportValidity(); }

    constructor() {
        super();
        this.internals_ = this.attachInternals();

        this.value_ = '';
        this.isLoading = false;
        this.previewMode = false;
        this.previewContent = '';
        this.selectionStart = 0;
        this.selectionEnd = 0;
        this.preventCursorReset = false;

        this.undoStack = [];
        this.redoStack = [];
        /**
         *  This is a list containing the information for each plugin
         *  It includes the id, name, before and after text for each plugin
         *  The before and after text is used to wrap the selected text in the textarea
         *  This is not always ideal since some plugins are different between inline and block level use
         * */
        this.plugins = [
            {
                'id': 'b',
                'name': 'B',
                'before': '**',
                'after': '**'
            },
            {
                'id': 'i',
                'name': 'I',
                'before': '*',
                'after': '*'
            },
            {
                'id': 's',
                'name': 'S',
                'before': '~~',
                'after': '~~'
            },
            {
                'id': 'h1',
                'name': 'H1',
                'before': '# ',
                'after': ''
            },
            {
                'id': 'link',
                'name': 'Link',
                'before': '[',
                'after': '](http://)'
            },
            {
                'id': 'image',
                'name': 'Image',
                'before': '![Alt Text](',
                'after': ')'
            },
            {
                'id': 'code',
                'name': 'Code',
                'before': '`',
                'after': '`'
            },
            {
                'id': 'quote',
                'name': 'Quote',
                'before': '> ',
                'after': ''
            },
            {
                'id': 'list',
                'name': 'List',
                'before': '- ',
                'after': '',
            },
            {
                'id': 'spoiler',
                'name': 'Spoiler',
                'before': '>! ',
                'after': '!<'
            },
            {
                'id': 'hr',
                'name': 'HR',
                'before': '---',
                'after': ''
            },
        ];
    }

    static styles = css`
        :host {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 100%;
        }
        :host * {
            flex-grow: 1;
            box-sizing: border-box;
        }
        .loading {
            opacity: 0.6;
            pointer-events: none;
        }
        .hidden {
            display: none;
        } 
        .md-preview-label {
            display: block;
            margin-top: 0.5em;
            margin-bottom: 0.5em;
            background-color: var(--button-bg-color-hover, #4E432E);
            border-radius: var(--border-radius-base, 6px);
            box-shadow: 0px 2px 5px 0px rgba(0,0,0,0.2);
            cursor: pointer;
            font-weight: bold;
            text-align: center;
            padding: 0.5em;
            width: 100%;
        }
        .md-preview-label input {
            display: none;
        }
        .md-preview {
            border: 2px solid var(--link-color, #A89368);
            background-color: var(--input-bg-color, #222222);
            color: var(--input-color, #FFF);
            border-radius: 5px;
            min-height: 300px;
            padding-left: 1em;
            padding-right: 1em;
            width: 100%;
        } 
        textarea {
            background-color: var(--input-bg-color, #222222);
            color: var(--input-color, #FFF);
            padding: 8px 12px;
            height: 100%;
            min-height: 255px;
            width: 100%;
            
            border: 2px solid var(--link-color, #A89368);
            background-color: var(--input-bg-color, #222222);
            color: var(--input-color, #FFF);
            border-radius: 5px;
            min-height: 300px;
        }
        .md-codes {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: left;
            gap: 0.5em;
            width: 100%;
            margin-top: 0.5em;
            margin-bottom: 0.5em;
        }
        .md-codes .md-button {
            flex-grow: 0;
            background-color: var(--button-bg-color-hover, #4E432E);
            color: var(--body-color, #B9B5AF);
            border-radius: var(--border-radius-base, 6px);
            border: 2px solid var(--link-color, #A89368);
            box-shadow: 0px 2px 5px 0px rgba(0,0,0,0.2);
            padding: 0.5em;
            cursor: pointer;
            font-weight: bold;
            text-align: center;
        }
    `;

    // connectedCallback
    connectedCallback() {
        super.connectedCallback();
        this.captureState(); // Capture initial state
    }

    captureState() {
        const state = {
            value: this.value_,
            selectionStart: this.selectionStart,
            selectionEnd: this.selectionEnd,
        };
        this.undoStack.push(state);
        // Limit the undo stack size to prevent memory issues
        if (this.undoStack.length > 50) {
            this.undoStack.shift();
        }
    }

    applyState(state) {
        console.log('Applying state', state);
        this.value_ = state.value;
        this.selectionStart = state.selectionStart;
        this.selectionEnd = state.selectionEnd;
        this.updateTextarea();
    }

    updateTextarea() {
        console.log('Updating textarea', this.selectionEnd, this.selectionStart);
        const textArea = this.shadowRoot.querySelector('textarea');
        textArea.value = this.value_;
        textArea.setSelectionRange(this.selectionEnd, this.selectionEnd);
        this.internals_.setFormValue(this.value_);
    }

    handleUndo() {
        if (this.undoStack.length > 0) {
            const currentState = {
                value: this.value_,
                selectionStart: this.selectionStart,
                selectionEnd: this.selectionEnd,
            };
            this.redoStack.push(currentState);
            const prevState = this.undoStack.pop();
            this.applyState(prevState);
        }
    }

    handleRedo() {
        if (this.redoStack.length > 0) {
            const state = this.redoStack.pop();
            this.captureState(); // Capture current state for undo before applying redo
            this.applyState(state);
        }
    }

    keyEvents(event) {
        if (event.ctrlKey || event.metaKey) {
            switch (event.key) {
                case 'z':
                    if (event.shiftKey) {
                        this.handleRedo();
                    } else {
                        this.handleUndo();
                    }
                    event.preventDefault();
                    break;
                case 'y':
                    this.handleRedo();
                    event.preventDefault();
                    break;
            }
        }
    }

    handleInput(event) {
        this.captureState(); // Capture state before making changes
        this.value_ = event.target.value;
        this.internals_.setFormValue(this.value_);
        this.selectionStart = event.target.selectionStart;
        this.selectionEnd = event.target.selectionEnd;
    }

    updateSelection(event) {
        if (this.preventCursorReset) {
            return;
        }
        this.selectionStart = event.target.selectionStart;
        this.selectionEnd = event.target.selectionEnd;
    }

    togglePreview() {
        this.previewMode = !this.previewMode;
        if (this.previewMode) {
            this.captureState(); // Capture state before switching modes
            this.fetchPreview();
            return;
        }


        setTimeout(() => {
            // textarea focus
            const textArea = this.shadowRoot.querySelector('textarea');
            textArea.focus({preventScroll: true});
            this.updateTextarea();
        }, 100);
    }

    fetchPreview() {
        this.isLoading = true;
        const xhr = new XMLHttpRequest();
        let csrftoken = getCookie('csrftoken');
        xhr.open('POST', '/api/markdown/preview/');
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader("X-CSRFToken", csrftoken);

        xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.DONE) {
                if (xhr.status === 200) {
                    this.previewContent = xhr.responseText;
                } else {
                    this.previewContent = 'Error fetching preview';
                }
                this.isLoading = false;
            }
        };
        xhr.send(JSON.stringify({ message: this.value_ }));
    }

    markdownButtonClicked(plugin) {
        if (this.previewMode) {
            return false;
        }
        this.captureState(); // Capture state before making changes
        let textBefore = this.value_.substring(0, this.selectionStart);
        let textAfter = this.value_.substring(this.selectionEnd);
        let selectedText = this.value_.substring(this.selectionStart, this.selectionEnd);

        let newText = '';
        if (selectedText) {
            newText = textBefore + plugin.before + selectedText + plugin.after + textAfter;
        } else {
            newText = textBefore + plugin.before + plugin.after + textAfter;
        }

        this.value_ = newText;
        this.internals_.setFormValue(this.value_);

        // Set flag before updating the cursor position
        this.preventCursorReset = true;

        // After inserting, focus the textarea and set cursor position
        let textArea = this.shadowRoot.querySelector('textarea');
        textArea.focus({preventScroll: true});

        setTimeout(() => {
            this.preventCursorReset = false;
            let newCursorPos = textBefore.length + plugin.before.length + selectedText.length + plugin.after.length;
            textArea.setSelectionRange(newCursorPos, newCursorPos);
            this.redoStack = []; // Clear redo stack after a change
        }, 100);
    }

    render() {
        return html`
            <div class="md-codes">
                ${this.plugins.map(plugin => html`<button class="md-button" @click=${{handleEvent: () => this.markdownButtonClicked(plugin)}}>${plugin.name}</button>`)}
            </div>
            <textarea
                class="${this.isLoading || this.previewMode ? 'loading' : ''}"
                .value="${this.value_}"
                @focus="${this.updateSelection}"
                @select="${this.updateSelection}"
                @input="${this.handleInput}"
                @keydown="${this.keyEvents}"
                ?disabled="${this.previewMode}"
            ></textarea>
            <label class="md-preview-label">
                <input type="checkbox" @change="${this.togglePreview}" ?checked="${this.previewMode}">
                ${this.togglePreview ? html`📃 Show` : html`📄 Hide`} Preview
            </label>
            <div class="md-preview ${this.previewMode ? '' : 'hidden'}">
                ${this.isLoading ? html`<p>Loading...</p>` : html`${unsafeHTML(this.previewContent)}`}
            </div>
        `;
    }
}
customElements.define('o3dl-md-input', MarkdownInput);